import { Search } from "lucide-react";
import { useContext, useEffect, useMemo, useState } from "react";
import { useCookies } from "react-cookie";
import { useNavigate } from "react-router-dom";

import { Button } from "@/app/components/ui/button";
import { Input } from "@/app/components/ui/input";
import type { ResumeContextValue } from "@/app/types/resume";

import ErrorToast from "../../../../components/ErrorToast";
import EditorListItems from "../../../../components/ResumeBuilder/EditorListItems";
import type { EditorListItem } from "../../../../components/ResumeBuilder/EditorListItems/EditorListItems";
import SectionActions from "../../../../components/ResumeBuilder/SectionActions";
import Spinner from "../../../../components/Spinner";
import TextEditor from "../../../../components/TextEditor";
import Tips, { type TipsContent } from "../../../../components/Tips";
import ResumeContext from "../../../../context/ResumeContext";
import { convertArrayToUl } from "../../../../handlers/utilityFunctions";
import { Accomplishment } from "../../../../models/resume/accomplishment";
import { StaticValue } from "../../../../seed/constants";
import { usePatchResumeSection } from "../../../../services/create-resume/queries";
import { RESUME_SECTIONS } from "../../../../services/create-resume/resumeSections";
import accomplishmentListSuggestions from "./accomplishmentList";

interface PatchLedger {
	accomplishments: { data: Parameters<typeof convertArrayToUl>[0] };
}

interface PatchError {
	response: { data: { message: string | string[] } };
}

const accomplishmentsTips: TipsContent = {
	summary: "Most notable achievements, three to five bullet points.",
	tips: [
		"Professional Experience section include more details of the “highlights”.",
		"Underneath the position information, list your most impressive accomplishments in bullets not paragraphs. Shoot for 3 to 5 bullets.",
		"If you only held one or two positions and require more bullets for each position to fill out your resume content → Just make sure the additional accomplishments you highlight are relevant to the job description and carry the momentum you've established in previous bullets.",
		{
			text: "Describe your accomplishments in terms of results, not responsibilities:",
			items: [
				"Relevant → to job you're applying for; start with most to least relevant",
				"Relative → want to compare you performance to established targets",
				"Results-oriented → Most quantifiable the better; articulate into tangible value statements [time/cost saved, increased speed/revenue etc.]",
			],
		},
		"Recommend leaving off any work experience that took place 15+ years ago. Only exception is if the position is your current role OR the experience is more relevant to position you're applying to than your current role.",
	],
};

const Accomplishments = () => {
	const navigate = useNavigate();

	const staticValue = new StaticValue();
	const staticValues = staticValue.getDefaultValues();
	const patchSection = usePatchResumeSection();
	const [cookies] = useCookies(["resume"]);
	const [accomplishmentList, setAccomplishmentList] = useState<EditorListItem[]>([...accomplishmentListSuggestions]);

	// Reported under the editor it belongs to, not as a toast that leaves the
	// screen before the reader has looked away from the button.
	const [accomplishmentsError, setAccomplishmentsError] = useState("");

	const { resumeData, setResumeData } = useContext(ResumeContext) as ResumeContextValue;
	const [serverErrors, setServerErrors] = useState<string[]>([]);

	const [accomplishments, setAccomplishments] = useState(resumeData.extras.accomplishments);
	const updateAccomplishments = (value: string) => {
		setAccomplishmentsError("");
		setAccomplishments(value);
	};

	const [userInput, setUserInput] = useState(resumeData.contactInfo.resumeTitle);

	// The search box was rendered with no handler behind it, so typing in it did
	// nothing. Narrowing a derived copy is safe: `EditorListItems` reads each row's
	// `added` state off the editor's own content, not off this array.
	const [search, setSearch] = useState("");
	const shownAccomplishments = useMemo(
		() => accomplishmentList.filter((item) => item.title.toLowerCase().includes(search.trim().toLowerCase())),
		[accomplishmentList, search],
	);

	//submission handler
	const addAccomplishments = () => {
		// The message used to be raised and then ignored: the empty section was sent
		// anyway and the page moved on, so the reader saw a complaint about a save
		// that had already gone through.
		if (accomplishments === "") {
			setAccomplishmentsError("Please add your accomplishments");
			return;
		}

		const payload = new Accomplishment(accomplishments);
		const request = payload.getAccomplishmentRequest();

		const resumeId = cookies[staticValues.localStorageResumeId];

		patchSection.mutate({ resumeId, section: RESUME_SECTIONS.ACHIEVEMENTS, data: request } as never, {
			onSuccess: successPatchAccomplishment,
			onError: errorPatchAccomplishment,
		});
	};

	function successPatchAccomplishment(ledger: PatchLedger) {
		const resArray = ledger.accomplishments.data;

		const accomplishmentList = convertArrayToUl(resArray);

		setResumeData((prevState) => {
			return {
				...prevState,
				extras: {
					...prevState.extras,
					accomplishments: accomplishmentList,
				},
			};
		});

		navigate("/resume/extras");
	}

	function errorPatchAccomplishment(data: Error) {
		const serverResponse = (data as unknown as PatchError).response.data;
		Array.isArray(serverResponse.message)
			? setServerErrors((prev) => {
					return [...prev, ...serverResponse.message];
				})
			: setServerErrors((prev) => {
					return [...prev, serverResponse.message as string];
				});
	}

	useEffect(() => {
		setUserInput(resumeData.contactInfo.resumeTitle);
	}, [resumeData]);

	return (
		<div className="min-h-screen">
			{serverErrors.map((item, index) => {
				return <ErrorToast error={item} key={index} />;
			})}
			<div className="grid grid-cols-1 gap-6 md:grid-cols-2">
				<div>
					<div className="mb-3 flex items-start justify-between gap-3">
						<p className="text-gray-700">Tell us about your accomplishments?</p>
						<Tips content={accomplishmentsTips} />
					</div>

					<div className="mb-4">
						<div className="flex">
							<Input
								type="text"
								className="rounded-r-none"
								placeholder={userInput}
								value={search}
								onChange={(event) => setSearch(event.target.value)}
							/>
							<Button type="button" className="rounded-l-none md:px-10" aria-label="Search">
								<Search className="size-4" />
							</Button>
						</div>
					</div>
					<EditorListItems
						editorListItems={shownAccomplishments}
						editorContent={accomplishments}
						setEditorContent={updateAccomplishments}
						setEditorListItems={setAccomplishmentList}
					/>
				</div>
				<div className="mt-4 flex flex-col justify-end">
					<TextEditor
						value={accomplishments}
						setValue={updateAccomplishments}
						placeholder="Write about your accomplishments here..."
					/>
					{accomplishmentsError && (
						<div className="mt-1 font-medium text-red-500 text-sm leading-tight">{accomplishmentsError}</div>
					)}
				</div>
			</div>
			<SectionActions className="flex items-center justify-end gap-3">
				<Button
					type="button"
					variant="secondary"
					onClick={() => {
						navigate("/resume/extras");
					}}
				>
					Cancel
				</Button>
				<Button type="button" onClick={addAccomplishments}>
					{patchSection.isPending ? (
						<>
							Loading <Spinner />
						</>
					) : (
						"Save"
					)}
				</Button>
			</SectionActions>
		</div>
	);
};

export default Accomplishments;
